import { useMemo } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, GridOptions } from 'ag-grid-community';
import { useLanguage } from '@/shared/hooks/useLanguage';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';

interface DataTableProps<T = any> {
  rowData: T[];
  columnDefs: ColDef[];
  defaultColDef?: ColDef;
  gridOptions?: GridOptions;
  height?: string | number;
  className?: string;
  pagination?: boolean;
  paginationPageSize?: number;
}

const DataTable = <T,>({
  rowData,
  columnDefs,
  defaultColDef,
  gridOptions,
  height = '600px',
  className = '',
  pagination = true,
  paginationPageSize = 10,
}: DataTableProps<T>) => {
  const { isRTL, language } = useLanguage();

  const defaultColDefMerged: ColDef = useMemo(
    () => ({
      sortable: true,
      filter: true,
      resizable: true,
      ...defaultColDef,
    }),
    [defaultColDef]
  );

  const localeText = useMemo(
    () => ({
      page: language === 'fa' ? 'صفحه' : 'Page',
      more: language === 'fa' ? 'بیشتر' : 'More',
      to: language === 'fa' ? 'تا' : 'to',
      of: language === 'fa' ? 'از' : 'of',
      next: language === 'fa' ? 'بعدی' : 'Next',
      last: language === 'fa' ? 'آخرین' : 'Last',
      first: language === 'fa' ? 'اولین' : 'First',
      previous: language === 'fa' ? 'قبلی' : 'Previous',
      loadingOoo: language === 'fa' ? 'در حال بارگذاری...' : 'Loading...',
      noRowsToShow: language === 'fa' ? 'هیچ داده‌ای یافت نشد' : 'No rows to show',
      pageSizeSelectorLabel: language === 'fa' ? 'تعداد در صفحه:' : 'Page size:',
    }),
    [language]
  );

  const mergedGridOptions: GridOptions = useMemo(
    () => ({
      ...gridOptions,
      localeText,
      suppressMenuHide: true,
      enableRtl: isRTL,
    }),
    [gridOptions, localeText, isRTL]
  );

  return (
    <div
      className={`ag-theme-alpine ag-grid-dark-mode ${className}`}
      style={{ height, width: '100%' }}
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      <AgGridReact
        rowData={rowData}
        columnDefs={columnDefs}
        defaultColDef={defaultColDefMerged}
        gridOptions={mergedGridOptions}
        pagination={pagination}
        paginationPageSize={paginationPageSize}
        enableRtl={isRTL}
      />
    </div>
  );
};

export default DataTable;

